import { createFetch } from 'ext/lib/api/fetch-client';
import type { ApiClient, AnalyticsQueryParams } from 'ext/lib/api/types';

interface AuthConfig {
  accountId: string;
  /** Token can be a static string or a function that returns a fresh token */
  token: string | (() => Promise<string> | string);
}

export interface AnalyticsApiOptions {
  /** Override for Customer API base URL (used for analytics endpoints) */
  customerApiUrl?: string | undefined;
}

const createAnalyticsApiQuery = (
  { accountId, token }: AuthConfig,
  options: AnalyticsApiOptions = {}
) => {
  const customerApiUrl =
    options.customerApiUrl ?? import.meta.env.CUSTOMER_API_URL;

  const fetchAnalyticsAPI = createFetch(`${customerApiUrl}/v2`, {
    accountId,
    token,
  });

  return (query: AnalyticsQueryParams) =>
    fetchAnalyticsAPI(`analytics/query`, {
      method: 'POST',
      body: JSON.stringify({
        account_id: accountId,
        ...query,
      }),
    });
};

export const getAnalyticsApiEndpoints = (
  auth: AuthConfig,
  options: AnalyticsApiOptions = {}
): Partial<ApiClient> => {
  const queryAnalyticsAPI = createAnalyticsApiQuery(auth, options);

  return {
    getEventCounts: (eventNames, { start = '7 days ago', end = Date.now() }) =>
      queryAnalyticsAPI({
        metrics: ['events'],
        start_time: start,
        end_time: end,
        dimensions: ['event'],
        conditions: [['event', 'in', eventNames]],
      }),

    getClickstreamEventCount: (
      identifiers: string[],
      {
        start = '7 days ago',
        end = Date.now(),
        interaction = 'click',
      }: {
        start?: string | number;
        end?: string | number;
        interaction?: string;
      }
    ) =>
      queryAnalyticsAPI({
        metrics: ['count'],
        start_time: start,
        end_time: end,
        dimensions: ['identifier'],
        conditions: [
          ['interaction', '==', interaction],
          ['identifier', 'in', identifiers],
        ],
      }),
  };
};
